/**
 * "Items" section of the series editor (edit mode only): drag-and-drop ordered
 * table of the series' items with thumbnail, title / slug, episode, date, status,
 * media glyphs and Edit / Duplicate actions, plus loading, error and empty states.
 * Render inside <EditorShell> (styles of MediathekEditor.scss).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { MDBBtn, MDBIcon, MDBSpinner, MDBTable, MDBTableHead } from 'mdb-react-ui-kit';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { getLocalized, videoProvider, youtubeThumbnailUrl } from '../../../utils/mediathek';
import { EditorMain, EditorSection } from '../MediathekEditorLayout';
import { MediaThumb } from '../MediaSlots';
import { DuplicateButton, StatusBadge, seriesTypeIcon } from '../MediathekShared';

/** Equal row height whether the title takes one or two lines (48px thumbnail + padding); set on the always visible handle cell. */
const ROW_CELL_STYLE = { height: '64px' };
/** Title column: remaining width; max-width 0 lets the text truncate instead of widening the table. */
const TITLE_CELL_STYLE = { width: '100%', maxWidth: 0 };
/** Two lines at most, then an ellipsis. */
const TITLE_CLAMP_STYLE = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minWidth: 0 };
const TABULAR_STYLE = { fontVariantNumeric: 'tabular-nums' };

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');

/** Icon + tooltip + screen-reader text of one media kind. */
function MediaGlyph({ icon, brand = false, label }) {
  return (
    <span title={label}>
      <MDBIcon fab={brand} fas={!brand} icon={icon} aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

function MediaGlyphs({ entry, hideEmpty = false }) {
  const { t } = useTranslation();
  const external = typeof entry.externalVideoUrl === 'string' ? entry.externalVideoUrl.trim() : '';
  const provider = external ? videoProvider({ externalVideoUrl: external }) : null;
  const glyphs = [];
  if (entry.audio?.s3Path) glyphs.push(<MediaGlyph key="audio" icon="headphones" label={t('Audio')} />);
  if (entry.video?.s3Path) glyphs.push(<MediaGlyph key="video" icon="film" label={t('Video file')} />);
  if (external) {
    let icon = 'link';
    if (provider === 'youtube') icon = 'youtube';
    else if (provider === 'vimeo') icon = 'vimeo-v';
    glyphs.push(<MediaGlyph key="external" icon={icon} brand={icon !== 'link'} label={t('External video')} />);
  }
  if (glyphs.length === 0) return hideEmpty ? null : <span className="me-muted" aria-hidden="true">–</span>;
  return <span className="me-media-glyphs">{glyphs}</span>;
}

/**
 * @param {{
 *   seriesId: string,
 *   seriesType: string,
 *   items: object[],
 *   publishedCount: number,
 *   isLoadingItems: boolean,
 *   isReordering: boolean,
 *   loadFailed: boolean,
 *   onReorder: (fromIndex: number, toIndex: number) => void,
 *   onRetry: () => void,
 * }} props
 */
export default function SeriesItemsSection({
  seriesId,
  seriesType,
  items,
  publishedCount,
  isLoadingItems,
  isReordering,
  loadFailed,
  onReorder,
  onRetry,
}) {
  const { t, i18n } = useTranslation();
  const seriesPath = `/admin/mediathek/series/${seriesId}`;
  const newItemPath = `${seriesPath}/items/create`;
  const returnState = { from: seriesPath };
  const glyph = seriesTypeIcon(seriesType);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    onReorder(result.source.index, result.destination.index);
  };

  const newItemButton = (
    <Link to={newItemPath} className="btn btn-primary btn-sm">
      <MDBIcon fas icon="plus" className="me-2" aria-hidden="true" />
      {t('New item')}
    </Link>
  );

  let body;
  if (isLoadingItems) {
    body = (
      <div className="text-center py-4">
        <MDBSpinner color="primary" role="status">
          <span className="visually-hidden">{t('Loading...')}</span>
        </MDBSpinner>
      </div>
    );
  } else if (loadFailed && items.length === 0) {
    body = (
      <div className="me-empty">
        <span className="me-empty-glyph" aria-hidden="true"><MDBIcon fas icon="exclamation-triangle" /></span>
        <span>{t('The items of this series could not be loaded')}</span>
        <MDBBtn color="primary" outline size="sm" type="button" onClick={onRetry}>
          <MDBIcon fas icon="redo" className="me-2" aria-hidden="true" />
          {t('Retry')}
        </MDBBtn>
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div className="me-empty">
        <span className="me-empty-glyph" aria-hidden="true"><MDBIcon fas icon={glyph} /></span>
        <span>{t('This series has no items yet.')}</span>
        <Link to={newItemPath} className="btn btn-primary btn-sm">
          <MDBIcon fas icon="plus" className="me-2" aria-hidden="true" />
          {t('Create the first item')}
        </Link>
      </div>
    );
  } else {
    body = (
      <>
        <p className="me-help mt-0 mb-3 d-flex flex-wrap align-items-center gap-2">
          <span>
            {t('Drag to reorder. The order is saved immediately (sort key 10, 20, ...). Items without a sort key are shown first.')}
          </span>
          {isReordering && (
            <span className="d-inline-flex align-items-center gap-2" role="status">
              <MDBSpinner size="sm" role="presentation" />
              <span>{t('Saving the new order...')}</span>
            </span>
          )}
        </p>
        <DragDropContext onDragEnd={handleDragEnd}>
          <MDBTable hover responsive align="middle" small className="mb-0">
            <MDBTableHead>
              <tr>
                <th style={{ width: '40px' }}><span className="visually-hidden">{t('Drag to reorder')}</span></th>
                <th className="d-none d-sm-table-cell" style={{ width: '64px' }}><span className="visually-hidden">{t('Image')}</span></th>
                <th>{t('Title')}</th>
                <th className="text-end text-nowrap d-none d-md-table-cell">{t('Episode')}</th>
                <th className="text-nowrap d-none d-md-table-cell">{t('Published at')}</th>
                <th className="d-none d-md-table-cell">{t('Status')}</th>
                <th className="d-none d-sm-table-cell">{t('Media')}</th>
                <th className="text-end"><span className="visually-hidden">{t('Actions')}</span></th>
              </tr>
            </MDBTableHead>
            <Droppable droppableId="mediaItems" isDropDisabled={isReordering}>
              {(droppable) => (
                <tbody ref={droppable.innerRef} {...droppable.droppableProps}>
                  {items.map((entry, index) => {
                    const title = getLocalized(entry.title, i18n.language) || t('(untitled)');
                    const itemPath = `/admin/mediathek/items/${entry.id}`;
                    const fallbackThumb = entry.image?.fileName ? undefined : youtubeThumbnailUrl(entry.externalVideoUrl) || undefined;
                    const hasEpisode = entry.episodeNumber !== null && entry.episodeNumber !== undefined && entry.episodeNumber !== '';
                    return (
                      <Draggable key={entry.id} draggableId={entry.id} index={index} isDragDisabled={isReordering}>
                        {(draggable, snapshot) => (
                          <tr
                            ref={draggable.innerRef}
                            {...draggable.draggableProps}
                            style={{
                              ...draggable.draggableProps.style,
                              display: snapshot.isDragging ? 'table' : undefined,
                              tableLayout: snapshot.isDragging ? 'fixed' : undefined,
                            }}
                            className={snapshot.isDragging ? 'shadow-3' : ''}
                          >
                            <td
                              {...draggable.dragHandleProps}
                              className="px-1 text-center"
                              style={{ ...ROW_CELL_STYLE, cursor: isReordering ? 'progress' : 'grab' }}
                              title={t('Drag to reorder')}
                              aria-label={`${t('Drag to reorder')}: ${title}`}
                            >
                              <span className="me-drag-handle" aria-hidden="true">
                                <MDBIcon fas icon="grip-vertical" />
                              </span>
                            </td>
                            <td className="d-none d-sm-table-cell">
                              <MediaThumb fileName={entry.image?.fileName} url={fallbackThumb} icon={glyph} />
                            </td>
                            {/* Takes the remaining width and truncates instead of widening the table
                                (not while dragging: the lifted row uses a fixed table layout) */}
                            <td style={snapshot.isDragging ? undefined : TITLE_CELL_STYLE}>
                              <div className="d-flex align-items-start gap-2">
                                <Link to={itemPath} state={returnState} className="me-row-title text-reset" style={TITLE_CLAMP_STYLE} title={title}>
                                  {title}
                                </Link>
                                {entry.isHero && (
                                  <span className="text-warning flex-shrink-0" title={t('Hero')}>
                                    <MDBIcon fas icon="star" aria-hidden="true" />
                                    <span className="visually-hidden">{t('Hero')}</span>
                                  </span>
                                )}
                              </div>
                              {entry.slug && (
                                <span className="me-row-sub text-truncate d-none d-md-block" title={entry.slug}>{entry.slug}</span>
                              )}
                              {/* Below md the episode / date / status columns (below sm also media) are hidden: shown stacked here */}
                              <span className="me-row-sub d-flex d-md-none flex-wrap align-items-center gap-2">
                                <StatusBadge status={entry.status} />
                                {hasEpisode && <span className="text-nowrap">{`${t('Episode')} ${entry.episodeNumber}`}</span>}
                                {entry.publishedAt && <span className="text-nowrap">{formatDate(entry.publishedAt)}</span>}
                                {/* Below sm the media column is hidden too */}
                                <span className="d-inline-flex d-sm-none"><MediaGlyphs entry={entry} hideEmpty /></span>
                              </span>
                            </td>
                            <td className="text-end text-nowrap d-none d-md-table-cell pe-3" style={TABULAR_STYLE}>
                              {hasEpisode ? entry.episodeNumber : '–'}
                            </td>
                            <td className="text-nowrap d-none d-md-table-cell" style={TABULAR_STYLE}>
                              {formatDate(entry.publishedAt)}
                            </td>
                            <td className="text-nowrap d-none d-md-table-cell"><StatusBadge status={entry.status} /></td>
                            <td className="text-nowrap d-none d-sm-table-cell"><MediaGlyphs entry={entry} /></td>
                            <td className="text-end text-nowrap ps-0">
                              <span className="me-row-actions">
                                <Link
                                  to={itemPath}
                                  state={returnState}
                                  className="btn btn-link btn-sm px-2"
                                  title={t('Edit')}
                                  aria-label={`${t('Edit')}: ${title}`}
                                >
                                  <MDBIcon fas icon="pen" aria-hidden="true" />
                                </Link>
                                <DuplicateButton variant="icon" item={entry} from={seriesPath} disabled={isReordering} className="px-2" label={`${t('Duplicate')}: ${title}`} />
                              </span>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    );
                  })}
                  {droppable.placeholder}
                </tbody>
              )}
            </Droppable>
          </MDBTable>
        </DragDropContext>
      </>
    );
  }

  return (
    <EditorMain className="mt-4">
      <EditorSection
        id="series-items"
        icon="list-ol"
        title={t('Items')}
        description={isLoadingItems || (loadFailed && items.length === 0)
          ? undefined
          : t('{{itemCount}} items, {{publishedCount}} published', { itemCount: items.length, publishedCount })}
        tools={items.length > 0 ? newItemButton : undefined}
      >
        {body}
      </EditorSection>
    </EditorMain>
  );
}
